// Lib
import { FC, useEffect, useMemo, useState } from "react";
// Api
import { useGetServerLogsQuery } from "rtkQuery/query/serverLogsAPI";
// Hooks
import { useNotification, useTable, useViewport } from "hooks";
// Types
import { ETable } from "types/tableFilters";
// Constants
import { rtkQueryParams } from "consts";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { DescriptionBlackIcon } from "icons";
// Components
import { Modal, Table } from "components";
// Styled
import { FlexContainer, PageWrapper } from "styled/Box";
import { Typography } from "styled/Typography";

import { columns } from "./config";
import { TableAction } from "types/common";
import { ServerLogItem } from "types/serverLogs";
import { ErrorCodeModal } from "../components";

export const ServerLogs: FC = () => {
  const { openNotification } = useNotification();

  const { isDesktop } = useViewport();

  const [cursor, setCursor] = useState<string | null>(null);
  const [errorModal, setErrorModal] = useState<string | null>(null);

  const { debouncedFiltersQuery } = useTable({
    name: ETable.ServerLogs,
  });

  const {
    data: serverLogs,
    isFetching: isServerLogsLoading,
    error: serverLogsError,
  } = useGetServerLogsQuery(
    {
      query: {
        ...(debouncedFiltersQuery && debouncedFiltersQuery),
        limit: 30,
        cursor,
      },
    },
    rtkQueryParams,
  );

  useEffect(() => {
    if (serverLogsError) {
      errorHandler({ error: serverLogsError, openNotification });
    }
  }, [serverLogsError]);

  const handleFetchNextPage = () => {
    if (serverLogs?.lastEvaluatedKey) {
      setCursor(serverLogs.lastEvaluatedKey);
    }
  };

  const actions: TableAction[] = useMemo(
    () => [
      {
        title: "Error Code",
        type: "Grey",
        Icon: DescriptionBlackIcon,
        loadingId: "",
        disableFunction: (row: ServerLogItem) => !row?.rawError,
        onClick: (row: ServerLogItem) => setErrorModal(row?.rawError),
      },
    ],
    [],
  );

  return (
    <PageWrapper $fullwidth $column>
      <FlexContainer
        $fullwidth
        $column
        $grow={1}
        $padding={isDesktop && "0 0 32px"}
      >
        <FlexContainer
          $padding="0 0 24px"
          $align="center"
          $justify="space-between"
        >
          <Typography.H1>Server Logs</Typography.H1>
        </FlexContainer>

        <Table
          isLoading={isServerLogsLoading}
          dataSource={serverLogs?.items || []}
          columns={columns}
          actions={actions}
          rowKey="sk"
          empty={{
            icon: DescriptionBlackIcon,
            title: "No logs to show.",
          }}
          infiniteScroll={{
            canFetchNextPage: !!serverLogs?.hasMore,
            fetchNextPage: handleFetchNextPage,
          }}
        />
      </FlexContainer>

      <ErrorCodeModal error={errorModal} onClose={() => setErrorModal(null)} />
    </PageWrapper>
  );
};
